import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Container, IconButton, Paper, Stack, Tab, Tabs, TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useApi } from '../../hooks/useApi';
import DevicesTab from './DevicesTab';
import HistoryTab from './HistoryTab';
import ProgramsTab from './ProgramsTab';
import RulesTab from './RulesTab';
import SettingsTab from './SettingsTab';
import { DEFINITIONS_FILES_NOTE, FILES_NOTE, POLICY_URL, deleteUrl, definitionsEditable, devicesUrl, errorText, historyUrl, policyUrl, rulesEditable } from './policyEdit';

const TABS = ['rules', 'machines', 'programs', 'settings', 'history'];
const ACCOUNT_KEY = 'softwarePolicyAccount';

const readStored = (key) => {
  try { return sessionStorage.getItem(key); } catch (ex) { return null; }
};
const store = (key, value) => {
  try { sessionStorage.setItem(key, value); } catch (ex) { /* a private window: the choice is just not remembered */ }
};

// The software policy of one account, shaped like the endpoint schedules: rule sets, each a filter on VES labels (account is myorg is the
// whole account) and the rules for the machines that match, the machines VES lists, how to recognise and fix each program, and the settings. Every change is checked by the API and kept in the history.
function SoftwarePolicyPage() {
  const jApi = useApi();
  const apiRef = useRef(jApi);
  apiRef.current = jApi;
  const theme = useTheme();
  const [params, setParams] = useSearchParams();

  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState(null);
  const [accountsError, setAccountsError] = useState('');
  const [data, setData] = useState({ state: 'idle' });         // { state, ...the API's answer } or { state: 'error', error }
  const [devices, setDevices] = useState({ state: 'loading' });      // the account's machines from VES, with the rule sets that apply to each
  const [history, setHistory] = useState({ state: 'loading' });
  const [scripts, setScripts] = useState([]);                     // the scripts VES has published, to choose from in a program's actions
  const [notice, setNotice] = useState('');
  const tab = TABS.includes(params.get('tab')) ? params.get('tab') : TABS[0];

  useEffect(() => {
    let cancelled = false;
    apiRef.current.request('GET', 'cma/account')
      .then((response) => {
        if (cancelled) return;
        const list = (response.data || []).slice().sort((a, b) => a.code.localeCompare(b.code));
        setAccounts(list);
        const wanted = params.get('account') || readStored(ACCOUNT_KEY);
        setAccount(list.find((a) => a.code === wanted) || list[0] || null);
      })
      .catch((ex) => { if (!cancelled) { console.error('Failed to load accounts:', ex); setAccountsError(errorText(ex)); } });
    apiRef.current.request('GET', 'cma/scripts')
      .then((response) => { if (!cancelled) setScripts(response.data?.scripts || []); })
      .catch((ex) => console.error('Failed to list the scripts:', ex));      // the picker then says to type the path
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async (code) => {
    if (!code) return;
    setData({ state: 'loading' });
    try {
      const response = await apiRef.current.request('GET', policyUrl(code));
      setData({ state: 'ok', ...response.data });
    } catch (ex) {
      console.error('Failed to load the software policy:', ex);
      setData({ state: 'error', error: errorText(ex) });
    }
    setDevices({ state: 'loading' });
    setHistory({ state: 'loading' });
    const readDevices = apiRef.current.request('GET', devicesUrl(code))
      .then((response) => setDevices({ state: 'ok', ...response.data }))
      .catch((ex) => setDevices({ state: 'error', error: errorText(ex) }));
    const readHistory = apiRef.current.request('GET', historyUrl(code))
      .then((response) => setHistory({ state: 'ok', items: response.data?.history || [] }))
      .catch((ex) => setHistory({ state: 'error', error: errorText(ex) }));
    await Promise.all([readDevices, readHistory]);
  }, []);

  useEffect(() => { setNotice(''); load(account?.code); }, [account, load]);

  // The tabs' way to change something. A failure throws, so the dialog can show the API's own reason.
  const save = useCallback(async (body) => {
    const response = await apiRef.current.request('POST', POLICY_URL, body);
    setNotice('Saved. The change is in the history.');
    await load(account.code);
    return response?.data;
  }, [account, load]);
  const remove = useCallback(async (id, docAccount) => {
    await apiRef.current.request('DELETE', deleteUrl(docAccount || account.code, id));
    setNotice('Deleted. The change is in the history.');
    await load(account.code);
  }, [account, load]);

  const chooseAccount = (value) => {
    setAccount(value);
    if (value) store(ACCOUNT_KEY, value.code);
    const next = new URLSearchParams(params);
    next.delete('account');
    setParams(next, { replace: true });
  };
  const setTab = (e, value) => {
    const next = new URLSearchParams(params);
    next.set('tab', value);
    setParams(next, { replace: true });
  };

  const ok = data.state === 'ok';
  const canEditPolicy = ok && rulesEditable(data);
  const canEditDefinitions = ok && definitionsEditable(data);
  const common = { account: account?.code, data };

  return (
    <Box sx={{ minHeight: '100vh', pb: 6 }}>
      <Container maxWidth="xl">
        <Box sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, background: alpha(theme.palette.background.paper, 0.6),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
              <HomeRoundedIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
            </Link>
            <Typography variant="body2" color="text.secondary">/</Typography>
            <Link to="/endpointdevices" style={{ textDecoration: 'none' }}>
              <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>Endpoint Devices</Typography>
            </Link>
            <Typography variant="body2" color="text.secondary">/</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Software policy</Typography>
          </Box>
        </Box>

        <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Software policy</Typography>
              <Typography variant="body2" color="text.secondary">
                Which programs the machines of an account must have, may have, or must not have, chosen by their labels like an endpoint schedule, and how each program is recognised and fixed.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Autocomplete
                size="small" sx={{ minWidth: 260 }} options={accounts} value={account} disableClearable
                getOptionLabel={(a) => (a.name ? `${a.code} - ${a.name}` : a.code)} isOptionEqualToValue={(a, b) => a.code === b.code}
                onChange={(e, value) => chooseAccount(value)} renderInput={(p) => <TextField {...p} label="Account" />}
              />
              <Tooltip title="Reload"><span><IconButton onClick={() => load(account?.code)} disabled={!account || data.state === 'loading'} size="small" aria-label="Reload"><RefreshIcon /></IconButton></span></Tooltip>
              <Button component={Link} to="/endpointdevices" startIcon={<ArrowBackIcon />}>Endpoint Devices</Button>
            </Stack>
          </Stack>
        </Paper>

        {accountsError && <Alert severity="error" sx={{ mb: 2 }}>{accountsError}</Alert>}
        {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}
        {ok && !canEditPolicy && <Alert severity="info" sx={{ mb: 2 }}>{FILES_NOTE}</Alert>}
        {ok && !canEditDefinitions && <Alert severity="info" sx={{ mb: 2 }}>{DEFINITIONS_FILES_NOTE}</Alert>}

        <Paper sx={{ borderRadius: 2 }}>
          <Tabs value={tab} onChange={setTab} aria-label="Software policy" sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
            <Tab value="rules" label="Rules" />
            <Tab value="machines" label="Machines" />
            <Tab value="programs" label="Programs" />
            <Tab value="settings" label="Settings" />
            <Tab value="history" label="History" />
          </Tabs>
          <Box sx={{ p: 2 }}>
            {!account && !accountsError && <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>}
            {account && data.state === 'loading' && <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>}
            {data.state === 'error' && <Alert severity="error">{data.error}</Alert>}
            {ok && tab === 'rules' && <RulesTab {...common} devices={devices} canEdit={canEditPolicy} onSave={save} onDelete={remove} />}
            {ok && tab === 'machines' && <DevicesTab devices={devices} />}
            {ok && tab === 'programs' && <ProgramsTab {...common} scripts={scripts} canEdit={canEditDefinitions} onSave={save} onDelete={remove} />}
            {ok && tab === 'settings' && <SettingsTab {...common} canEdit={canEditPolicy} onSave={save} />}
            {tab === 'history' && account && data.state !== 'loading' && <HistoryTab history={history} />}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default SoftwarePolicyPage;
